import React, { Suspense } from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { Form, Button, Row } from 'react-bootstrap'
import {
  i18nTranslate,
  getTranslatedShippingMethod,
  isExpressCheckout,
  getCheckoutGTMPageData,
  scrollState,
  isLATAM,
  isSubscriptionOverrideCheckoutPage,
  currencyFormat,
  isB2BAccount,
  isB2B2C,
  showLocationDetails,
  trackEnhancedCheckout,
  trackCustomCheckoutEvent,
  CHECKOUT_EVENT_TYPES,
  trackAddShippingInfoEvent,
  enableNewFeature,
  isFeatureEnabled,
} from 'src/utils'
import { pageNames } from 'src/routes/pathParams'
import {
  checkoutContainer,
  cartContainer,
  accountsContainer,
  nuskinSubscriptionContainer,
} from 'src/models'
import { toastState, modalState, overlayState } from 'src/views/components'
import { isPromoWalletEnabled } from 'src/views/components/CheckoutAccordions/deps'
import { showAllPromoCodesInCheckout } from 'config/appConfig/configUtils'
import TagManager from 'react-gtm-module'
import ExpressSectionHeader from '../../ExpressSectionHeader'
import RecipientDetails from '../RecipientDetails/RecipientDetails'
import './styles.scss'
import { convertToBoolean, APPConfig } from 'config/appConfig'
import ShippingRates from 'src/views/components/GoogleMap/ShippingRates'

const PICKUP_SHIPPING_METHOD_ID =
  APPConfig?.getAppConfig()?.pickupShippingMethodId || 'PickUpPoint'

// Lazy load Google Maps component only when needed
const LazyGooglemaps = React.lazy(
  () => import('src/views/components/GoogleMap/PickupLocations')
)

@observer
class ShippingMethodAccordion extends React.Component {
  enableDangerousGoodsMessage =
    convertToBoolean(APPConfig?.getAppConfig()?.enableDangerousGoodsMessage) ||
    false
  isRecipientFormEnabled = isFeatureEnabled('enablePickupRecipientForm')
  @observable pickUpLocationResponse = {}
  @observable allPickUpLocations = {}
  @observable isToMakeLocationCall = false
  @observable selectedShippingMethodId = null
  @observable isCallInProgress = false
  @observable showList = !!!isExpressCheckout()
  @observable showLoaderOnCheck = false
  @observable isDangerousGood = false
  @observable selectedPickupLocation = null
  @observable shippingRatesOpen = false
  @observable recipientDetails = {
    recipientName: '',
    phoneCountryCode: '',
    phoneNumber: '',
  }
  @observable isRecipientDetailsValid = false

  setShippingMethodTimer = null
  redirectToSubscriptionCartTimer = null
  redirectToCartTimer = null

  constructor(props) {
    super(props)
  }

  async componentDidMount() {
    this.selectShippingMethod()
    checkoutContainer.isToDisableSubmitOrderButton = true
    cartContainer?.cartResponse?.items?.forEach(item => {
      item.skus?.map(skuItem => {
        if (convertToBoolean(skuItem?.skuProperties?.dangerousGoods) === true) {
          this.isDangerousGood = true
        }
      })
    })

    this.getPickUpLocationsAddress()

    if (cartContainer?.cartResponse?.payments) {
      if (!isExpressCheckout()) {
        await checkoutContainer.deletePayment()
      }
    }
    if (isB2BAccount() === true && !isB2B2C()) {
      await accountsContainer.getContracts()
    }
    this.getSelectedShippingMethod()
    this.setAlertZeroTotValWithDisableZeroCheckout()
  }

  componentDidUpdate() {
    if (Object.keys(this.allPickUpLocations).length === 0) {
      this.getPickUpLocationsAddress()
    }
  }

  componentWillUnmount() {
    if (this.redirectToSubscriptionCartTimer) {
      clearTimeout(this.redirectToSubscriptionCartTimer)
    }
    if (this.redirectToCartTimer) {
      clearTimeout(this.redirectToCartTimer)
    }
    // Clear pickup location timeout to prevent memory leaks
    checkoutContainer.clearPickupLocationTimeout()
  }
  getPickUpLocationsAddress = async () => {
    // Prefetch all pickup locations for all shipping methods with a locationCode
    const shippingMethods =
      cartContainer?.cartResponse?.deliveryDetails?.methods || []
    for (const method of shippingMethods) {
      const locationCode = method?.properties?.locationCode
      const bopisValue = method?.properties?.BOPIS || false
      if (
        bopisValue === true &&
        locationCode &&
        !this.allPickUpLocations[locationCode]
      ) {
        this.allPickUpLocations[locationCode] =
          await checkoutContainer.getPickUpLocations(locationCode)
      }
    }
  }

  selectShippingMethod = () => {
    const shippingMethods =
      cartContainer?.cartResponse?.deliveryDetails?.methods || []
    const selectedShippingMethod = shippingMethods?.find(
      method => method?.isSelected
    )
    if (selectedShippingMethod?.name) {
      this.selectedShippingMethodId = selectedShippingMethod.id
    }

    //Fix for MDIGI-4990
    if (shippingMethods.length > 0) {
      const hasSelectedShippingMethod = shippingMethods?.some(
        method => method.isSelected === true
      )
      if (!hasSelectedShippingMethod && shippingMethods?.[0]?.id) {
        checkoutContainer.updateShippingMethod(shippingMethods?.[0]?.id)
      }
    }
  }

  getSelectedShippingMethod = async () => {
    const shippingMethods =
      cartContainer?.cartResponse?.deliveryDetails?.methods || []
    this.pickUpLocationResponse = {}
    const selectedShippingMethod = shippingMethods?.filter(
      shippingMethod => shippingMethod?.isSelected === true
    )
    const preFillData =
      nuskinSubscriptionContainer?.subscriptionPreferenceResponse?.shipping ||
      ''

    // this.props?.preFillData?.shipping
    if (preFillData && nuskinSubscriptionContainer?.isToShowBlackOut) {
      this.selectedShippingMethodId = preFillData?.id
      await this.makePickUpLocationCall(preFillData)
    } else if (selectedShippingMethod.length > 0) {
      this.selectedShippingMethodId = selectedShippingMethod[0].id
      await this.makePickUpLocationCall(selectedShippingMethod[0])
    }

    // In express flow, keep Shipping Method expanded for BOPIS methods so
    // recipient details can be filled without reopening the section.
    if (isExpressCheckout()) {
      const selectedMethod = shippingMethods?.find(method => method?.isSelected)
      const isBOPIS = selectedMethod?.properties?.BOPIS === true
      const isRecipientAlreadyPatched =
        !!cartContainer?.cartResponse?.properties?.recipientName
      // Only expand to show the form when recipient has not been patched yet
      if (
        isBOPIS &&
        this.isRecipientFormEnabled &&
        !isRecipientAlreadyPatched
      ) {
        this.showList = true
      }
    }
  }

  makePickUpLocationCall = async selectedMethod => {
    const { properties = {} } = selectedMethod
    const bopisValue = properties?.BOPIS || false
    const locationCode = properties?.locationCode || ''
    this.isToMakeLocationCall = bopisValue === true && locationCode !== ''

    if (this.isToMakeLocationCall === true) {
      this.pickUpLocationResponse =
        await checkoutContainer.getPickUpLocations(locationCode)
    }
  }

  handlePickupLocationSelect = async location => {
    await checkoutContainer.handlePickupLocationSelect(location, {
      setSelectedPickupLocation: value => {
        this.selectedPickupLocation = value
      },
      setSelectedShippingMethodId: value => {
        this.selectedShippingMethodId = value
      },
      setShowList: value => {
        this.showList = value
      },
      handleClick: this.handleClick,
    })
  }

  handleClick = async id => {
    this.isCallInProgress = true
    const products = cartContainer?.cartResponse?.items || []
    const { title } = this.props
    const isExpressFlow = isExpressCheckout()

    if (isExpressFlow) {
      trackEnhancedCheckout({
        type: 'checkout',
        action: { step: isLATAM() ? '3' : '2', option: title },
        products: products,
      })
    }
    if (isExpressFlow && this.showLoaderOnCheck) {
      overlayState.showLoader()
    }

    if (id?.toLowerCase() !== PICKUP_SHIPPING_METHOD_ID?.toLowerCase()) {
      this.selectedPickupLocation = null

      // Clear pickupLocation property from cart properties
      const currentPickupLocation =
        cartContainer?.cartResponse?.properties?.pickupLocation

      if (currentPickupLocation) {
        const loadParams = {
          endPointName: 'updateBagAttributes',
          method: 'PATCH',
          postData: {
            properties: {
              pickupLocation: '',
            },
          },
        }

        await checkoutContainer.fetchResponse(loadParams)
      }
    }

    let response = await checkoutContainer.updateShippingMethod(id)
    const { userId = '', cartId = '', accountsId = '' } = response
    const paymentName = cartContainer?.cartResponse?.payments?.[0]?.name || ''
    const paymentId = cartContainer?.cartResponse?.payments?.[0]?.id || ''
    if (cartId && paymentName && paymentName.toLowerCase() === 'razorpay') {
      await checkoutContainer.deletePaymentById(paymentId)
      const bagValue = Math.abs(
        cartContainer?.cartResponse?.paymentValue?.totalAmountUnpaid || ''
      )
      if (paymentName?.toLowerCase() != 'creditcard' && bagValue > 0) {
        const unpaidPaymentResponse =
          await checkoutContainer.handleUnpaidPayment(paymentName, bagValue)
        await cartContainer.viewCart()
      }
    }
    let isSubscriptionCheckoutPage =
      isSubscriptionOverrideCheckoutPage() || false

    response?.items?.forEach(item => {
      item &&
        item.skus &&
        item.skus?.map(bundleItem => {
          let inventoryProperties = bundleItem.inventoryProperties
          if (
            (inventoryProperties?.available === false &&
              inventoryProperties?.backOrdered === true) ||
            (inventoryProperties?.available === false &&
              inventoryProperties?.backOrdered === false) ||
            (inventoryProperties?.available === false &&
              inventoryProperties?.backOrdered === null)
          ) {
            toastState.setToastMessage(
              i18nTranslate(
                'checkout.backOrder',
                'Sorry, this item is on backorder or out of stock.'
              ),
              false
            )
            if (isSubscriptionCheckoutPage) {
              this.redirectToSubscriptionCartTimer = setTimeout(() => {
                let originUrl =
                  window?.location?.href?.split('/subscriptionCheckout') || []
                window.location.replace(
                  `${originUrl?.[0]}${pageNames?.subscriptionCart}`
                )
              }, 1000)
            } else {
              this.redirectToCartTimer = setTimeout(() => {
                let originUrl =
                  window?.location?.href?.split(pageNames.checkout) ||
                  window?.location?.href?.split(pageNames.expressCheckout) ||
                  []
                window.location.replace(
                  `${originUrl?.[0]}${pageNames?.viewCart}`
                )
              }, 1000)
            }
          }
        })
    })

    if (!checkoutContainer.isSuccessResponse(response)) {
      toastState.setToastMessage(response.message)
      if (isExpressCheckout() && this.showLoaderOnCheck) {
        overlayState.hideLoader()
      }
    } else {
      isPromoWalletEnabled() &&
        (await cartContainer.promoCodeList({
          fetchAllPromoCodes: showAllPromoCodesInCheckout() ? 'true' : 'false',
        }))
      await this.getSelectedShippingMethod()
      // Keep accordion open for BOPIS methods (where recipient details are needed)
      const selectedMethod =
        cartContainer?.cartResponse?.deliveryDetails?.methods?.find(
          method => method?.isSelected
        )
      const isBOPIS = selectedMethod?.properties?.BOPIS === true
      const isRecipientFormEnabled = isFeatureEnabled(
        'enablePickupRecipientForm'
      )
      if (isBOPIS && isRecipientFormEnabled) {
        this.showList = true
      } else if (
        id?.toLowerCase() === PICKUP_SHIPPING_METHOD_ID?.toLowerCase()
      ) {
        this.showList = true
      } else {
        this.showList = !isExpressCheckout()
      }
      if (isExpressCheckout() && this.showLoaderOnCheck) {
        scrollState.smoothScrollToModalElement(
          `#qa-checkout-shippingmethod-accordion`
        )
        overlayState.hideLoader()
      }
      if (isExpressCheckout()) {
        const isShippingInfoEventEnabled = enableNewFeature(
          'enableShippingInfoGTMEvent'
        )
        const cartData = cartContainer?.cartResponse

        if (isShippingInfoEventEnabled) {
          trackAddShippingInfoEvent({
            cartData,
          })
        } else {
          trackCustomCheckoutEvent({
            eventType: CHECKOUT_EVENT_TYPES.SHIPPING,
            cartResponse: cartData,
          })
        }
      }
    }

    this.isCallInProgress = false

    TagManager.dataLayer({
      dataLayer: {
        event: 'shippingMethodChangeTotal',
        ...getCheckoutGTMPageData(),
        userId,
        accountsId,
        cartId,
        selectedShippingMethodDetails: this.selectedShippingMethodId,
        platform: 'equinox',
      },
    })
    return response
  }

  handleNext = async () => {
    this.isCallInProgress = true
    const products = cartContainer?.cartResponse?.items || []
    const { title } = this.props
    trackEnhancedCheckout({
      type: 'checkout',
      action: { step: '2', option: title },
      products: products,
    })
    const isShippingInfoEventEnabled = enableNewFeature(
      'enableShippingInfoGTMEvent'
    )
    if (isShippingInfoEventEnabled) {
      trackAddShippingInfoEvent({
        cartData: cartContainer?.cartResponse,
      })
    } else {
      trackCustomCheckoutEvent({
        eventType: CHECKOUT_EVENT_TYPES.SHIPPING,
        cartResponse: cartContainer?.cartResponse,
      })
    }

    // Update shipping method with recipient details if BOPIS method is selected
    const shippingMethods =
      cartContainer?.cartResponse?.deliveryDetails?.methods || []
    const selectedMethod = shippingMethods?.find(method => method?.isSelected)
    const isBOPIS = selectedMethod?.properties?.BOPIS === true
    if (
      isBOPIS &&
      this.isRecipientFormEnabled &&
      this.isRecipientDetailsValid &&
      this.recipientDetails?.recipientName
    ) {
      // Call updateShippingMethod with recipient details to patch them
      const response = await checkoutContainer.updateShippingMethod(
        selectedMethod.id,
        null,
        this.recipientDetails
      )

      if (checkoutContainer.isSuccessResponse(response)) {
        this.showList = false
      } else {
        toastState.setToastMessage(
          response.message ||
            i18nTranslate(
              'checkout.recipientDetailsSaveError',
              'Failed to save recipient details. Please try again.'
            )
        )
        this.isCallInProgress = false
        return
      }
    }

    if (isB2BAccount() && !isB2B2C()) {
      await this.handleB2BCostCenterValidation()
    } else {
      this.props.onContinue()
    }
    this.isCallInProgress = false
  }

  handleB2BCostCenterValidation = async () => {
    const costCenterValidateResponse =
      await checkoutContainer.validateCostCenter()
    const errMessage = costCenterValidateResponse?.message || ''
    const isSubscriptionCheckoutPage =
      isSubscriptionOverrideCheckoutPage() || false
    if (!checkoutContainer.isSuccessResponse(costCenterValidateResponse)) {
      if (isSubscriptionCheckoutPage) {
        this.props.history.push(`${pageNames.subscriptionCart}`)
      } else {
        this.props.history.push(`${pageNames.viewCart}`)
      }
      const message = errMessage
        ? errMessage
        : i18nTranslate(
            'checkout.validatecostCenter',
            'Please select a valid cost center'
          )
      toastState.setToastMessage(message)
    } else {
      this.props.onContinue()
    }
  }

  handleRecipientDetailsChange = ({ isValid, values }) => {
    this.isRecipientDetailsValid = isValid
    this.recipientDetails = values
  }

  isFromBlackOutViewDisable = (selectedShippingMethodId, id) => {
    return nuskinSubscriptionContainer?.isToShowBlackOut &&
      selectedShippingMethodId !== id
      ? true
      : false
  }
  // TODO EX: need to check  the removed code
  renderShippingMethods = () => {
    //const currencyCode = get(cartContainer, 'cartResponse.currencyCode')
    const shippingMethods =
      cartContainer.cartResponse?.deliveryDetails?.methods || []
    const isShippingRatesInfoEnabled =
      enableNewFeature('enableShippingRatesInfo') || false
    const selectedShippingMethod = shippingMethods.find(
      method => method.isSelected
    )
    if (shippingMethods?.length == 0 && isExpressCheckout()) {
      return (
        <>
          <ExpressSectionHeader
            title={i18nTranslate('checkout.shippingMethod', 'Shipping Method')}
            disableAccordion={true}
          />

          <div className="emptyShipping-method p-3 mb-2">
            {i18nTranslate(
              'checkout.emptyShippingMethod',
              'Shipping Method will be available once you enter shipping address'
            )}
          </div>
        </>
      )
    }

    const shippingLabel = (
      id,
      price,
      name,
      styleClasses = 'justify-content-between'
    ) => {
      const translatedMethodName = getTranslatedShippingMethod(
        id,
        isExpressCheckout(),
        name
      )

      const enableGoogleMaps = isFeatureEnabled('metapackIntegration')

      const shippingMethodName = enableGoogleMaps
        ? translatedMethodName && translatedMethodName.length > 0
          ? translatedMethodName
          : name || ''
        : getTranslatedShippingMethod(id, isExpressCheckout(), name) || ''

      const showMapView =
        id?.toLowerCase() === PICKUP_SHIPPING_METHOD_ID?.toLowerCase()

      // Parse pickup points from stringified JSON
      let locationDetails = []
      if (enableGoogleMaps && showMapView) {
        try {
          const pickupPointsString =
            cartContainer?.cartResponse?.deliveryDetails?.properties
              ?.pickupPoints
          if (pickupPointsString) {
            locationDetails = JSON.parse(pickupPointsString)
          }
        } catch (error) {
          console.error('Error parsing pickup points:', error)
        }
      }

      if (isExpressCheckout()) {
        return (
          <div
            className="d-flex flex-column express-header-body"
            data-testid="qa-express-shipping-method-body">
            {shippingMethodName && (
              <div className={`d-flex ${styleClasses}`}>
                <span
                  className="font-weight-400"
                  data-testid="qa-express-shipping-method-name">
                  {Array.isArray(shippingMethodName)
                    ? shippingMethodName[0]
                    : shippingMethodName}
                </span>
                <span
                  className="font-weight-bold"
                  data-testid="qa-express-shipping-method-price">
                  {price}
                </span>
              </div>
            )}
            {Array.isArray(shippingMethodName) && shippingMethodName[1] && (
              <span
                className="shippingDesciption"
                data-testid="qa-express-shipping-method-description">
                {shippingMethodName[1] || ''}
              </span>
            )}
            {enableGoogleMaps && showMapView && this.showList && (
              <div>
                {this.selectedPickupLocation ? (
                  <div className="selected-location-details">
                    <div className="pickup-from-style">
                      {i18nTranslate('selected.pickupLocation', 'Pickup from:')}
                    </div>
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                      <div>{this.selectedPickupLocation.name}</div>
                      {this.selectedPickupLocation.street && (
                        <div>{this.selectedPickupLocation.street}</div>
                      )}
                      {this.selectedPickupLocation.city && (
                        <div>{this.selectedPickupLocation.city}</div>
                      )}
                      {this.selectedPickupLocation.postalCode && (
                        <div>{this.selectedPickupLocation.postalCode}</div>
                      )}
                      {this.selectedPickupLocation.carrier && (
                        <div>{this.selectedPickupLocation.carrier}</div>
                      )}

                      <span
                        className="change-location-click"
                        data-testid="qa-change-pickup-location"
                        onClick={() => (this.selectedPickupLocation = null)}
                        onMouseEnter={e =>
                          (e.target.style.textDecoration = 'underline')
                        }
                        onMouseLeave={e =>
                          (e.target.style.textDecoration = 'none')
                        }>
                        {i18nTranslate('change.location', 'CHANGE LOCATION')}
                      </span>
                    </div>
                  </div>
                ) : (
                  <Suspense
                    fallback={
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        {i18nTranslate(
                          'checkout.pickupLocation.loadingGoogleMaps',
                          'Loading pickup locations...'
                        )}
                      </div>
                    }>
                    <LazyGooglemaps
                      locationDetails={locationDetails}
                      onLocationSelect={this.handlePickupLocationSelect}
                    />
                  </Suspense>
                )}
              </div>
            )}
          </div>
        )
      }
      return `${shippingMethodName} ${price}`
    }

    const getShippingMethodClassName = selected => {
      if (isExpressCheckout()) {
        return selected
          ? 'express-checkout-check express-checkout-selected '
          : 'express-checkout-check '
      }
      return ' '
    }
    const getBlackOutViewClassName = isDisabled => {
      return nuskinSubscriptionContainer?.isToShowBlackOut && !isDisabled
        ? 'black-out-view-disabled'
        : ''
    }

    return (
      <div className="shipping-method-wrapper">
        {isExpressCheckout() && (
          <ExpressSectionHeader
            title={i18nTranslate('checkout.shippingMethod', 'Shipping Method')}
            dataTestId="qa-express-shipping-method-header"
            isOpen={this.showList}
            onClick={() => {
              this.showList = !this.showList
              this.showLoaderOnCheck = true
              if (!this.selectedShippingMethodId) {
                this.selectShippingMethod()
              }
            }}
          />
        )}

        {/* alert message for dangerousgoods */}

        {this.enableDangerousGoodsMessage && this.isDangerousGood && (
          <div className="d-flex flex-row dangerous-goods-message my-2 flex-wrap">
            {i18nTranslate(
              'dangerous_goods_cart_item_shipping_limitation',
              'Some items in your cart might limit the shipping options.'
            )}
          </div>
        )}
        <div className="shipping-method-title">
          {this.showList &&
            shippingMethods &&
            shippingMethods?.map(shippingMethod => {
              const {
                name,
                id,
                cost,
                isSelected,
                properties = {},
              } = shippingMethod
              //if case to hide storepick from API
              if (id.toLowerCase() !== 'storepickup') {
                const shippingCost = currencyFormat({
                  // currency: currencyCode,
                  price: cost,
                })
                const locationCode = properties?.locationCode
                const bopisValue = properties?.BOPIS || false
                // Build className string safely
                let className = ''
                if (nuskinSubscriptionContainer?.isToShowBlackOut) {
                  className +=
                    getShippingMethodClassName(
                      this.selectedShippingMethodId === id
                    ) + ' '
                } else {
                  className += getShippingMethodClassName(isSelected) + ' '
                }
                if (isExpressCheckout()) {
                  className += !this.showList ? 'mb-4 border-bottom ' : ''
                } else {
                  className += 'mb-2 shipping-inner-wrapper '
                }
                className += getBlackOutViewClassName(
                  this.selectedShippingMethodId === id
                )
                return (
                  <div key={id} className="shipping-method-item">
                    <div
                      className={className}
                      data-testid={
                        isSelected
                          ? 'qa-ship-method-selected'
                          : 'qa-ship-method'
                      }>
                      <input
                        tabIndex={0}
                        type="radio"
                        name="shippingMethodRadio"
                        id={`methods-${name}`}
                        checked={
                          nuskinSubscriptionContainer?.isToShowBlackOut
                            ? this.selectedShippingMethodId === id
                            : isSelected
                        }
                        disabled={this.isFromBlackOutViewDisable(
                          this.selectedShippingMethodId,
                          id
                        )}
                        onChange={() => {
                          this.setShippingMethodTimer = setTimeout(() => {
                            this.handleClick(id, cost)
                          }, 1500)
                        }}
                        onKeyDown={event => {
                          // enabling navigation with the keyboard's left (37), up (38), right (39), and down (40) arrow keys
                          if (
                            (event?.keyCode === 37 ||
                              event?.keyCode === 38 ||
                              event?.keyCode === 39 ||
                              event?.keyCode === 40) &&
                            this.setShippingMethodTimer
                          ) {
                            clearTimeout(this.setShippingMethodTimer)
                          }
                        }}
                        key={`shippingmethod-${name}`}
                      />
                      <label
                        htmlFor={`methods-${name}`}
                        className="form-check-label">
                        <div>{shippingLabel(id, shippingCost, name)}</div>
                      </label>
                      {/* For ExpressCheckout, Always show location details if available for this method */}
                      {bopisValue === true &&
                        locationCode &&
                        isExpressCheckout() &&
                        showLocationDetails(
                          this.allPickUpLocations[locationCode]
                        )}

                      {bopisValue === true &&
                        isExpressCheckout() &&
                        this.isRecipientFormEnabled &&
                        isSelected && (
                          <RecipientDetails
                            initialValues={this.recipientDetails}
                            onChange={this.handleRecipientDetailsChange}
                          />
                        )}
                    </div>
                    {/* Always show location details if available for this method */}
                    {bopisValue === true &&
                      locationCode &&
                      !isExpressCheckout() &&
                      showLocationDetails(
                        this.allPickUpLocations[locationCode]
                      )}
                  </div>
                )
              }
            })}
          {isShippingRatesInfoEnabled &&
            this.showList &&
            shippingMethods?.length > 0 && (
              <>
                <div className="pickup-details-content">
                  {i18nTranslate(
                    'checkout.pickupLocation.expressShippingNotice',
                    'EXPRESS SHIPPING is not available for devices with lithium batteries'
                  )}
                </div>
                <Button
                  className="pickup-details-popup-content"
                  variant="outline-primary"
                  onClick={() => (this.shippingRatesOpen = true)}>
                  {i18nTranslate(
                    'checkout.pickupLocation.shippingRatesInfo',
                    'SHIPPING RATES AND DELIVERY TIMES'
                  )}
                </Button>
              </>
            )}
        </div>
        {isExpressCheckout() && !this.showList && (
          <div className="w-100">
            {shippingLabel(
              selectedShippingMethod?.id,
              currencyFormat({
                price: selectedShippingMethod?.cost,
              }),
              selectedShippingMethod?.name,
              'gap-1 shippingDesciption font-weight-bold text-dark'
            )}
            {this.renderCollapsedPickupLocation(shippingMethods)}
          </div>
        )}
        {isShippingRatesInfoEnabled && (
          <ShippingRates
            isInfoModalEnabled={this.shippingRatesOpen}
            toggleScanInfoModal={() => (this.shippingRatesOpen = false)}
          />
        )}
      </div>
    )
  }

  renderCollapsedPickupLocation = shippingMethods => {
    // Find the pickup method specifically (not just selected method)
    const pickupMethod = shippingMethods.find(
      method =>
        method?.id?.toLowerCase() === PICKUP_SHIPPING_METHOD_ID?.toLowerCase()
    )

    if (!pickupMethod || !pickupMethod.isSelected) {
      return null
    }

    let selectedLocation = null
    try {
      const pickUpData = cartContainer?.cartResponse?.properties?.pickupLocation
      if (pickUpData && pickUpData.trim() !== '') {
        if (typeof pickUpData === 'object') {
          selectedLocation = pickUpData
        } else if (typeof pickUpData === 'string') {
          selectedLocation = JSON.parse(pickUpData)
        }
      }
    } catch (error) {
      console.error('Error parsing pickUpLocation:', error)
    }

    if (!selectedLocation && this.selectedPickupLocation) {
      selectedLocation = this.selectedPickupLocation
    }

    if (selectedLocation) {
      const addressParts = [
        selectedLocation.name,
        selectedLocation.street,
        selectedLocation.city,
        selectedLocation.postalCode,
      ].filter(Boolean)
      return (
        <div
          className="mt-1 shippingDesciption"
          style={{ fontSize: '12px', color: '#666' }}>
          {addressParts.join(', ')}
        </div>
      )
    }
    return null
  }

  setAlertZeroTotValWithDisableZeroCheckout = () => {
    const isSubscriptionOverrideCheckout =
      isSubscriptionOverrideCheckoutPage() || false
    const redirectPath = isSubscriptionOverrideCheckout
      ? pageNames.subscriptionCart
      : pageNames.viewCart
    if (checkoutContainer.isZeroTotValWithDisableZeroCheckout()) {
      modalState.setModalMessage(
        i18nTranslate(
          'shipping.alertZeroTotValWithDisableZeroCheckout',
          'Zero value checkout is not allowed. Please update your cart to proceed further.'
        ),
        i18nTranslate('modaView.buttonText', 'OK'),
        this.props,
        redirectPath
      )
    }
  }

  renderContinueButton = () => {
    // Check if the selected method has BOPIS property set to true
    const shippingMethods =
      cartContainer?.cartResponse?.deliveryDetails?.methods || []
    const selectedMethod = shippingMethods?.find(method => method?.isSelected)
    const isBOPIS = selectedMethod?.properties?.BOPIS === true

    // For express checkout, only show the Continue button when the shipping
    // method list is open and BOPIS + recipient form are active.
    // This keeps button visibility in sync with form visibility.
    const isRecipientFormShown =
      this.isRecipientFormEnabled && isBOPIS && this.showList
    if (isExpressCheckout() && !isRecipientFormShown) {
      return ''
    }

    // disable button when bopis is selected and recipient data is not valid
    const shouldDisableForPickup =
      isBOPIS && this.isRecipientFormEnabled && !this.isRecipientDetailsValid

    return (
      <Row
        className="justify-content-end shipping-method-continue-row"
        noGutters>
        <Button
          className="rounded-0 px-5 py-2 shipping-method-continue btn-bg-blue ml-auto"
          onClick={this.handleNext}
          disabled={
            this.isCallInProgress ||
            !this.selectedShippingMethodId ||
            shouldDisableForPickup
          }
          data-testid="qa-ship-methods-continue">
          {i18nTranslate('shippingMethod.continue', 'Continue')}
        </Button>
      </Row>
    )
  }

  render() {
    return (
      <Form>
        {this.renderShippingMethods()}
        {this.renderContinueButton()}
      </Form>
    )
  }
}

export { ShippingMethodAccordion }
export default ShippingMethodAccordion
